<script setup>
import {onMounted, nextTick, ref, watch} from 'vue';
import {useRouter} from 'vue-router';
import {useI18n} from 'vue-i18n';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import ServiceRequirementsListComponent from '@/components/services-requirements/ServiceRequirementsListComponent.vue';
import ServiceRequirementService from '@/services/ServiceRequirementService.js';
import BoCreateServiceRequirementModal from '@/components/modals/BoCreateServiceRequirementModal.vue';

const {t} = useI18n();
const router = useRouter();

const STORAGE_KEY = 'BO-SERVICE-REQUIREMENTS-TAB';

/** Výchozí tab se načte z localStorage, fallback na "k řešení". */
const activeTab = ref(localStorage.getItem(STORAGE_KEY) ?? 'active');
watch(activeTab, val => localStorage.setItem(STORAGE_KEY, val));

/** Stats pro dashboard dlaždice — null = načítání. */
const stats = ref(null);

const tabGroupRef = ref(null);
const activeListRef = ref(null);

onMounted(() => {
    ServiceRequirementService.getStats().then(data => {
        stats.value = data;
    });
    // Srovná šířku tab tlačítek na maximum ze dvou — CSS to neumí bez JS,
    // protože btn-primary má globální min-width: 200px a texty mají různou délku.
    nextTick(() => {
        const btns = tabGroupRef.value?.querySelectorAll('button');
        if (btns?.length) {
            const maxW = Math.max(...[...btns].map(b => b.scrollWidth));
            btns.forEach(b => (b.style.minWidth = maxW + 'px'));
        }
    });
});

function openCreateModal() {
    const {reveal, onConfirm} = createConfirmDialog(BoCreateServiceRequirementModal);
    reveal();
    // Po potvrzení (úspěšném odeslání) refreshneme statistiky a tabulku aktivních požadavků
    onConfirm(() => {
        ServiceRequirementService.getStats().then(data => { stats.value = data; });
        activeListRef.value?.reload();
    });
}
</script>

<template>
    <div class="page-section">
        <div class="page-section__heading">
            <div class="row">
                <div class="col-3">
                    <a @click="router.back()" class="page-section__heading__back">
                        <i class="mdi mdi-arrow-left"></i> {{ t('serviceRequirementsHistory.back') }}
                    </a>
                </div>
                <div class="col-6 text-center">
                    <div class="page-section__title">{{ t('boServiceRequirements.title') }}</div>
                </div>
                <div class="col-3 d-flex justify-content-end align-items-center">
                    <button class="btn btn-primary" @click="openCreateModal">
                        <i class="mdi mdi-plus me-1"></i>
                        {{ t('boServiceRequirements.new_request') }}
                    </button>
                </div>
            </div>
        </div>
    </div>

    <div class="page-section__content">
        <!-- Dashboard dlaždice -->
        <div class="row gy-3 mb-4">
            <div class="col-12 col-md-4" v-for="tile in [
                { key: 'total',      label: t('boServiceRequirements.stat_total') },
                { key: 'unassigned', label: t('boServiceRequirements.stat_unassigned') },
                { key: 'open',       label: t('boServiceRequirements.stat_open') },
            ]" :key="tile.key">
                <div class="card card--big-value h-100">
                    <div class="card-body d-flex flex-column justify-content-center">
                        <div class="card__value__label">{{ tile.label }}</div>
                        <!-- Skeleton během načítání, číslo po načtení -->
                        <div class="card__value">
                            <Skeletor v-if="stats === null" width="40" height="28" />
                            <span v-else>{{ stats[tile.key] }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Tab přepínač -->
        <div ref="tabGroupRef" class="d-flex gap-2 mb-4">
            <button
                class="btn btn-normal"
                :class="activeTab === 'active' ? 'btn-primary' : 'btn-outline-tenant'"
                @click="activeTab = 'active'"
            >
                {{ t('boServiceRequirements.tab_active') }}
            </button>
            <button
                class="btn btn-normal"
                :class="activeTab === 'resolved' ? 'btn-primary' : 'btn-outline-tenant'"
                @click="activeTab = 'resolved'"
            >
                {{ t('boServiceRequirements.tab_resolved') }}
            </button>
        </div>

        <!-- Tabulka se přepíná dle aktivního tabu -->
        <ServiceRequirementsListComponent
            v-if="activeTab === 'active'"
            ref="activeListRef"
            :resolved="false"
            storage-key="SERVICE-REQUIREMENTS-ACTIVE"
        />
        <ServiceRequirementsListComponent
            v-else
            :resolved="true"
            storage-key="SERVICE-REQUIREMENTS-ARCHIVE"
        />
    </div>
</template>

<style scoped>
/* Stat dlaždice — větší číslo a čitelnější titulek než výchozí card--big-value hodnoty */
.card__value {
  font-size: 2.5em;
}
.card__value__label {
  font-size: 1.1em !important;
  opacity: 1;
  min-height: unset;
}
</style>
